import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CashFundsService, CostCenterSummary, Identity } from '@libs/core/api';
import { ModalController } from '@ionic/angular';
import { NgxAuthService } from 'ngx-auth-utils';
import { of, ReplaySubject } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { presentBillingInfoModal } from '../../billing/present-billing-info-modal';

@Component({
  selector: 'ui-project-context-card',
  templateUrl: './project-context-card.component.html',
  styleUrls: ['./project-context-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectContextCardComponent {
  private readonly project$ = new ReplaySubject<CostCenterSummary | null>(1);
  private _project?: CostCenterSummary;

  @Input()
  set project(value: CostCenterSummary | undefined) {
    this._project = value;
    this.project$.next(value?.id ? value : null);
  }

  get project(): CostCenterSummary | undefined {
    return this._project;
  }

  @Input() user!: Identity;
  @Input() rounded = false;
  @Input() buyer? = false;
  @Input() superBuyer? = false;

  constructor(
    private readonly modalController: ModalController,
    private readonly authService: NgxAuthService,
    private readonly cashFundsService: CashFundsService
  ) {}

  readonly firstResidualLimit$ = this.project$.pipe(
    switchMap((project) =>
      project?.id
        ? this.cashFundsService
            .getCardLimits({ projectId: project.id })
            .pipe(map((limits) => this.getFirstResidualLimit(limits)))
        : of(null)
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  getCompanyNameForProject(
    project: CostCenterSummary,
    user: Identity | null
  ): string {
    if (!user || !project) {
      return '';
    }

    const company = user.companies?.find((c) =>
      c.costCenters?.some((p) => p.id === project.id)
    );

    return company?.name ?? '';
  }

  get isSuperOperatore(): boolean {
    return this.project?.subtasks?.some((s) => s.superOperatore) ?? false;
  }

  async openBillingInfo(): Promise<void> {
    const project = this.project;
    if (!project) {
      return;
    }

    await presentBillingInfoModal(
      this.modalController,
      this.authService,
      project
    );
  }

  private getFirstResidualLimit(limits: {
    dailyLimit?: number | null;
    weeklyLimit?: number | null;
    monthlyLimit?: number | null;
    yearlyLimit?: number | null;
    allTimeLimit?: number | null;
  }): { labelKey: string; value: number } | null {
    const orderedLimits: Array<{ labelKey: string; value: number } | null> = [
      limits.dailyLimit != null
        ? { labelKey: 'pages.card-mgmt.limit.daily', value: limits.dailyLimit }
        : null,
      limits.weeklyLimit != null
        ? {
            labelKey: 'pages.card-mgmt.limit.weekly',
            value: limits.weeklyLimit,
          }
        : null,
      limits.monthlyLimit != null
        ? {
            labelKey: 'pages.card-mgmt.limit.monthly',
            value: limits.monthlyLimit,
          }
        : null,
      limits.yearlyLimit != null
        ? {
            labelKey: 'pages.card-mgmt.limit.yearly',
            value: limits.yearlyLimit,
          }
        : null,
      limits.allTimeLimit != null
        ? {
            labelKey: 'pages.card-mgmt.limit.allTime',
            value: limits.allTimeLimit,
          }
        : null,
    ];

    return (
      orderedLimits.find(
        (limit): limit is { labelKey: string; value: number } => limit !== null
      ) ?? null
    );
  }
}
